import { inject, Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { FsErrorResponse, FsTaskService, ProfileService } from '@fairandsmart/angular';
import {
  EntityReference, FsEventType, Task, TaskFilter, TaskStatus,
} from '@fairandsmart/types';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  EMPTY, from, Observable, of,
} from 'rxjs';
import {
  catchError, map, mergeMap, take, tap,
} from 'rxjs/operators';
import { AlertService } from '@Services/alert.service';
import { OrgaService } from '@Services/orga.service';
import { CommonActionTypes, CoreEvent } from '@Common/common.actions';
import { FsOrgState } from '../../../reducers';
import { TaskDetailData, TaskDetailDialogComponent, ViewMode } from './task-detail-dialog/task-detail-dialog.component';
import {
  AddCommentSuccess,
  AssignTask,
  AssignTaskSuccess,
  CancelTask,
  CancelTaskSuccess,
  CompleteTask,
  CompleteTaskSuccess,
  CountUserTasksToDo,
  CountUserTasksToDoFailure,
  CountUserTasksToDoSuccess,
  CreateRefTask,
  CreateRequestTask,
  CreateTaskSuccess,
  LoadRefTasks,
  LoadRefTasksFailure,
  LoadRefTasksSuccess,
  LoadUserTasks,
  LoadUserTasksFailure,
  LoadUserTasksSuccess,
  RefreshTasks,
  ShowTaskDetail,
  TaskActionTypes,
  UpdateTaskSuccess,
} from './task.actions';
import { TaskService } from './task.service';

@Injectable()
export class TaskEffects {
  readonly fsTaskService = inject(FsTaskService);

  readonly taskService = inject(TaskService);

  readonly profile = inject(ProfileService);

  readonly orgaService = inject(OrgaService);

  readonly alert = inject(AlertService);

  readonly dialog = inject(MatDialog);

  readonly actions$ = inject(Actions);

  readonly store = inject(Store<FsOrgState>);

  private state$<T>(project: (state: FsOrgState) => Observable<T>): Observable<T> {
    return this.store.pipe(
      take(1),
      mergeMap(project),
    );
  }

  showUserTasks = createEffect(() => this.actions$.pipe(
    ofType(
      TaskActionTypes.ShowUserTasks,
      TaskActionTypes.QuitEntityPage,
      TaskActionTypes.SelectUserTab,
    ),
    mergeMap(() => this.state$((state: FsOrgState) => {
      if (state.task.userTasks != null) {
        return EMPTY;
      }
      return of(new LoadUserTasks());
    })),
  ));

  loadUserTasks$ = createEffect(() => this.actions$.pipe(
    ofType(TaskActionTypes.LoadUserTasks),
    mergeMap(() => {
      const filter: TaskFilter = {
        assignee: this.profile.id,
        scope: this.orgaService.reference,
        limit: -1,
      };
      return this.fsTaskService.search(filter)
        .pipe(
          map((response) => new LoadUserTasksSuccess({ response })),
          catchError((response: FsErrorResponse) => of(new LoadUserTasksFailure({ response }))),
        );
    }),
  ));

  refreshTasks = createEffect(() => this.actions$.pipe(
    ofType(TaskActionTypes.RefreshTasks),
    mergeMap(() => this.state$((state: FsOrgState) => from([
      new LoadUserTasks(),
      new LoadRefTasks({ reference: state.task.currentRef }),
      new CountUserTasksToDo(),
    ]))),
  ));

  showRefTasks = createEffect(() => this.actions$.pipe(
    ofType(TaskActionTypes.ShowRequestTasks, TaskActionTypes.SelectRefTab),
    mergeMap(() => this.state$((state: FsOrgState) => {
      if (state.task.refTasks != null) {
        return EMPTY;
      }
      return of(new LoadRefTasks({ reference: state.task.currentRef }));
    })),
  ));

  loadRefTasks$ = createEffect(() => this.actions$.pipe(
    ofType(TaskActionTypes.LoadRefTasks),
    mergeMap((action: LoadRefTasks | AssignTaskSuccess) => this.state$((state: FsOrgState) => {
      let reference: EntityReference;
      if (action instanceof AssignTaskSuccess) {
        if (state.task.currentRef == null || state.task.currentRef !== action.payload.task.reference) {
          return EMPTY;
        }
        reference = state.task.currentRef;
      } else {
        reference = action.payload.reference;
      }
      const filter: TaskFilter = {
        reference,
        scope: this.orgaService.reference,
        limit: -1,
      };
      return this.fsTaskService.search(filter).pipe(
        map((response) => new LoadRefTasksSuccess({
          response,
          refIdentifier: reference,
        })),
        catchError((response: FsErrorResponse) => of(new LoadRefTasksFailure({ response }))),
      );
    })),
  ));

  countUserTasksToDo$ = createEffect(() => this.actions$.pipe(
    ofType(TaskActionTypes.CountUserTasksToDo),
    mergeMap((action: CountUserTasksToDo | AssignTaskSuccess | CreateTaskSuccess) => this.state$((state: FsOrgState) => {
      if (action instanceof CreateTaskSuccess) {
        if (action.payload.task.assignee !== this.profile.id && action.payload.task.reference !== state.task.currentRef) {
          return EMPTY;
        }
      } else if (action instanceof AssignTaskSuccess) {
        if (action.payload.previousAssignee !== this.profile.id && action.payload.task.assignee !== this.profile.id) {
          return EMPTY;
        }
      }
      const filter: TaskFilter = {
        assignee: this.profile.id,
        scope: this.orgaService.reference,
        status: [TaskStatus.ASSIGNED],
        limit: 0,
      };
      return this.fsTaskService.search(filter)
        .pipe(
          map((response) => new CountUserTasksToDoSuccess({ count: response.size })),
          catchError((response: FsErrorResponse) => of(new CountUserTasksToDoFailure({ response }))),
        );
    })),
  ));

  assignTask$ = createEffect(() => this.actions$.pipe(
    ofType(TaskActionTypes.AssignTask),
    mergeMap((action: AssignTask) => this.fsTaskService.assign(action.payload.taskId, action.payload.assignee)
      .pipe(
        tap(() => this.alert.success('TASK.ASSIGN.SUCCESS')),
        map((task) => new AssignTaskSuccess({ task, previousAssignee: action.payload.previousAssignee })),
        catchError((response: FsErrorResponse) => {
          this.alert.apiError(response.error, 'TASK.ASSIGN.ERROR');
          return EMPTY;
        }),
      )),
  ));

  cancelTask$ = createEffect(() => this.actions$.pipe(
    ofType(TaskActionTypes.CancelTask),
    mergeMap((action: CancelTask) => this.alert.promptConfirmDialog({
      data: {
        title: 'TASK.CANCEL.PROMPT.TITLE',
        content: 'TASK.CANCEL.PROMPT.MESSAGE',
        confirm: 'COMMON.BUTTONS.CONFIRM',
      },
    }).afterClosed().pipe(
      mergeMap((cancel: boolean) => {
        if (cancel) {
          return this.fsTaskService.cancel(action.payload.task.id, action.payload.variables).pipe(
            tap(() => this.alert.success('TASK.CANCEL.SUCCESS')),
            map((task) => new CancelTaskSuccess({ task })),
          );
        }
        return EMPTY;
      }),
      catchError((response: FsErrorResponse) => {
        this.alert.apiError(response.error, 'TASK.CANCEL.ERROR');
        return EMPTY;
      }),
    )),
  ));

  completeTask$ = createEffect(() => this.actions$.pipe(
    ofType(TaskActionTypes.CompleteTask),
    mergeMap((action: CompleteTask) => this.alert.promptConfirmDialog({
      data: {
        title: 'TASK.COMPLETE.PROMPT.TITLE',
        content: 'TASK.COMPLETE.PROMPT.MESSAGE',
        confirm: 'COMMON.BUTTONS.CONFIRM',
      },
    }).afterClosed().pipe(
      mergeMap((complete: boolean) => {
        if (complete) {
          return this.fsTaskService.complete(action.payload.task.id, action.payload.variables).pipe(
            tap(() => this.alert.success('TASK.COMPLETE.SUCCESS')),
            map((task) => new CompleteTaskSuccess({ task })),
          );
        }
        return EMPTY;
      }),
      catchError((response: FsErrorResponse) => {
        this.alert.apiError(response.error, 'TASK.COMPLETE.ERROR');
        return EMPTY;
      }),
    )),
  ));

  createUpdateAssignCommentCancelOrCompleteTaskSuccess$ = createEffect(() => this.actions$.pipe(
    ofType(
      TaskActionTypes.CreateTaskSuccess,
      TaskActionTypes.UpdateTaskSuccess,
      TaskActionTypes.AssignTaskSuccess,
      TaskActionTypes.AddCommentSuccess,
      TaskActionTypes.CancelTaskSuccess,
      TaskActionTypes.CompleteTaskSuccess,
    ),
    mergeMap((action: CreateTaskSuccess | UpdateTaskSuccess | AssignTaskSuccess | AddCommentSuccess
      | CancelTaskSuccess | CompleteTaskSuccess) => this.state$((state: FsOrgState) => {
        const effects = [];
        if (state.task.currentRef !== null && state.task.currentRef === action.payload.task.reference) {
          effects.push(new LoadRefTasks({ reference: state.task.currentRef }));
        }
        if (action.payload.task.assignee === this.profile.id
          || (action instanceof AssignTaskSuccess && action.payload.previousAssignee === this.profile.id)
        ) {
          effects.push(new LoadUserTasks());
          effects.push(new CountUserTasksToDo());
        }
        return from(effects);
      })),
  ));

  showTaskDetail$ = createEffect(
() => this.actions$.pipe(
    ofType(TaskActionTypes.ShowTaskDetail),
    mergeMap((action: ShowTaskDetail) => this.fsTaskService.get(action.payload.task.id).pipe(
      mergeMap((task: Task) => {
        this.dialog.open<TaskDetailDialogComponent, TaskDetailData>(TaskDetailDialogComponent, {
          data: {
            task,
            organisationMembers: this.orgaService.membersMap,
            mode: ViewMode.VIEW,
          },
          autoFocus: false,
        });
        return EMPTY;
      }),
      catchError((response: FsErrorResponse) => {
        this.alert.apiError(response.error, null);
        return EMPTY;
      }),
    )),
  ),
    { dispatch: false },
  );

  createTask$ = createEffect(() => this.actions$.pipe(
    ofType(TaskActionTypes.CreateRefTask, TaskActionTypes.CreateRequestTask),
    mergeMap((action: CreateRefTask | CreateRequestTask) => {
      this.dialog.open<TaskDetailDialogComponent, TaskDetailData>(TaskDetailDialogComponent, {
        data: {
          organisationMembers: this.orgaService.membersMap,
          scope: this.orgaService.reference,
          reference: action.payload.reference,
          mode: ViewMode.CREATE,
        },
      });
      return EMPTY;
    }),
  ), { dispatch: false });

  coreEvent$ = createEffect(() => this.actions$.pipe(
    ofType(CommonActionTypes.CoreEvent),
    mergeMap((action: CoreEvent) => {
      switch (action.payload.event.eventType) {
        case FsEventType.TASK_UPDATE:
        case FsEventType.TASK_COMPLETE:
        case FsEventType.TASK_CANCEL:
        case FsEventType.TASK_CREATE:
        case FsEventType.TASK_ASSIGN:
        case FsEventType.TASK_ADD_COMMENT:
        case FsEventType.TASK_UPDATE_COMMENT:
        case FsEventType.TASK_REMOVE_COMMENT: {
          return of(new RefreshTasks());
        }
        default:
          return EMPTY;
      }
    }),
  ));

  selectedTaskUpdated$ = createEffect(() => this.actions$.pipe(
    ofType(CommonActionTypes.CoreEvent),
    mergeMap((action: CoreEvent) => {
      switch (action.payload.event.eventType) {
        case FsEventType.TASK_UPDATE:
        case FsEventType.TASK_COMPLETE:
        case FsEventType.TASK_CANCEL:
        case FsEventType.TASK_ASSIGN:
        case FsEventType.TASK_ADD_COMMENT:
        case FsEventType.TASK_UPDATE_COMMENT:
        case FsEventType.TASK_REMOVE_COMMENT: {
          const openedTaskDialogs: MatDialogRef<TaskDetailDialogComponent, any>[] = this.dialog.openDialogs.filter((d) => d.componentInstance instanceof TaskDetailDialogComponent);
          if (openedTaskDialogs.length > 0) {
            const dialogComponent: TaskDetailDialogComponent = openedTaskDialogs[0].componentInstance;
            if (action.payload.event.sourceId === dialogComponent.task.id) {
              if (dialogComponent.mode === ViewMode.VIEW) {
                this.fsTaskService.get(dialogComponent.task.id).subscribe({
                  next: (task) => {
                    dialogComponent.task = task;
                    this.alert.info('TASK.ALERT.UPDATE_SUCCESS', { disableTimeOut: true });
                  },
                  error: (err) => console.error(err),
                });
              }
            }
            return EMPTY;
          }
          return EMPTY;
        }
        default:
          return EMPTY;
      }
    }),
  ), { dispatch: false });
}
